import React, { useState, useEffect } from "react";
import blogBg from "../../images/bg/blog-bg.png";

interface FaqItem {
  question: string;
  answer: string;
}

const faqs: FaqItem[] = [
  {
    question: "What industries does TorqAI serve?",
    answer: "TorqAI serves businesses across healthcare, real estate, e-commerce, financial services, hospitality, and professional services. Our AI agents are customizable to fit the unique workflows and compliance requirements of any industry."
  },
  {
    question: "How quickly can TorqAI agents be deployed?",
    answer: "Most TorqAI agents can be configured and deployed within days. Complex enterprise integrations may take 2-4 weeks. Our team handles integration, training, and optimization from day one."
  },
  {
    question: "Do I need technical expertise to use TorqAI?",
    answer: "Not at all. TorqAI products are designed with intuitive dashboards and no-code configuration. Our team provides full onboarding, training, and ongoing support so you can focus on your business."
  },
  {
    question: "What makes TorqAI different from other AI solutions?",
    answer: "TorqAI agents don't just follow scripts. They're grounded in your data, read the sentiment of every conversation, and take autonomous action. They adapt in real time to deliver results without waiting for a human to step in."
  },
  {
    question: "How does TorqAI pricing work?",
    answer: "We offer transparent, tiered pricing for each product: Starter, Growth, and Enterprise. Each tier is designed to scale with your business. Contact us for a custom quote tailored to your specific needs."
  },
  {
    question: "Is my data secure with TorqAI?",
    answer: "Yes. TorqAI is built on SOC 2-certified cloud infrastructure with end-to-end encryption and strict data isolation. Your data is never shared or used to train models for other clients."
  }
];

const FaqSection: React.FC = () => {
  const [activeIndex, setActiveIndex] = useState<number | null>(0);

  useEffect(() => {
    const bgEl = document.querySelector<HTMLElement>(".faq-section.bg_img");
    if (bgEl) {
      bgEl.style.backgroundImage = `url(${blogBg})`;
    }
  }, []);

  const toggleAccordion = (index: number) => {
    setActiveIndex(activeIndex === index ? null : index);
  };

  return (
    <section className="faq-section pt-130 pb-130 bg_img">
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": faqs.map(faq => ({
            "@type": "Question",
            "name": faq.question,
            "acceptedAnswer": {
              "@type": "Answer",
              "text": faq.answer
            }
          }))
        })}
      </script>
      <style>{`
        @media (max-width: 767px) {
          .faq-btn-mobile {
            padding: 20px 15px !important;
          }
          .faq-text-mobile {
            font-size: 14px !important;
            padding-right: 15px !important;
          }
          .faq-icon-mobile {
            width: 24px !important;
            height: 24px !important;
            min-width: 24px !important;
          }
          .faq-icon-mobile svg {
            width: 10px !important;
            height: 10px !important;
          }
        }
      `}</style>
      <div className="container">
        <div className="sec-title sec-title-center text-center mb-70 wow fadeInUp" data-wow-delay="100ms">
          <span className="sub-title">FAQs</span>
          <h2 className="title">Need Answers?</h2>
          <p style={{
            color: "rgb(255, 255, 255)",
            fontSize: "16px",
            lineHeight: "28px",
            fontWeight: "400",
            maxWidth: "600px",
            margin: "0 auto",
            marginTop: "20px",
            fontFamily: '"DM Sans", sans-serif'
          }}>
            Find answers to questions about AI services and how we help you grow.
          </p>
        </div>

        <div className="faq-wrapper wow fadeInUp" data-wow-delay="200ms" style={{ maxWidth: "900px", margin: "0 auto" }}>
          <div
            style={{
              background: "rgba(255, 255, 255, 0.03)",
              borderRadius: "16px",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              overflow: "hidden"
            }}
          >
            {faqs.map((faq, index) => (
              <div
                key={index}
                style={{
                  borderBottom: index !== faqs.length - 1 ? "1px solid rgba(255, 255, 255, 0.1)" : "none"
                }}
              >
                <button
                  className="faq-btn-mobile"
                  onClick={() => toggleAccordion(index)}
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    padding: "30px",
                    background: "transparent",
                    border: "none",
                    cursor: "pointer",
                    textAlign: "left",
                    transition: "all 0.3s ease"
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "20px" }}>
                    <span className="faq-text-mobile" style={{
                      fontSize: "17px",
                      fontWeight: "500",
                      color: "#fff",
                      fontFamily: "Inter, sans-serif"
                    }}>
                      {faq.question}
                    </span>
                  </div>
                  <div
                    className="faq-icon-mobile"
                    style={{
                      width: "32px",
                      height: "32px",
                      minWidth: "32px",
                      flexShrink: 0,
                      borderRadius: "50%",
                      border: "1px solid #ba6be7",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#ba6be7",
                      transition: "transform 0.3s ease",
                      transform: activeIndex === index ? "rotate(0deg)" : "rotate(0deg)"
                    }}
                  >
                    {activeIndex === index ? (
                      <svg width="14" height="2" viewBox="0 0 14 2" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 1H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                      </svg>
                    ) : (
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M7 1V13M1 7H13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                      </svg>
                    )}
                  </div>
                </button>
                <div
                  style={{
                    maxHeight: activeIndex === index ? "200px" : "0",
                    overflow: "hidden",
                    transition: "all 0.3s ease-in-out",
                    padding: activeIndex === index ? "0 30px 30px 30px" : "0 30px 0 30px",
                    opacity: activeIndex === index ? 1 : 0
                  }}
                >
                  <p style={{
                    color: "rgb(255, 255, 255)",
                    fontSize: "16px",
                    lineHeight: "28px",
                    fontWeight: "400",
                    margin: 0,
                    fontFamily: '"DM Sans", sans-serif'
                  }}>
                    {faq.answer}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FaqSection;
